import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { isNullish } from 'remeda';
import { GenericForm } from 'src/components/GenericForm/generic-form';
import { ModalContainer } from 'src/components/ModalContainer';
import { usePlatformEntities } from 'src/graphql';
import { mapAttributesDefinitionGroups } from 'src/graphql/entities/custom-attributes';
import {
  AttributeDefinition,
  CustomAttributeGroup,
  useCustomAttributeDefinitions,
} from 'src/graphql/entities/custom-attributes/custom-attributes';
import { useGlobalStore } from 'src/store';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { useNotifications } from 'src/utils/hooks';
import { parseJsonSafely } from 'src/utils/objects';
import { slugify } from 'src/utils/strings';
import { EntityContainer } from './entity-container';

export const ManageCustomAttributeGroupModal = () => {
  const { closeModal } = useModalMethods(Modals.ManageCustomAttributGroupModal);
  const { isOpened } = useModalState(Modals.ManageCustomAttributGroupModal);

  const { platformEntities } = usePlatformEntities();

  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();

  const { customAttributeDefinitions, saveCustomAttributeDefinition } =
    useCustomAttributeDefinitions({
      partnerId: selectedPartnerId,
    });

  const groups = mapAttributesDefinitionGroups(customAttributeDefinitions);

  const groupedGroupsByEntity = useMemo(
    () => Object.groupBy(groups, (definition) => definition.entity!),
    [groups],
  );

  const groupedGroupsByEntityAndPartner = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(groupedGroupsByEntity).map(([key, value]) => [
          key,
          Object.groupBy(value!, (group) => group.partnerId!),
        ]),
      ),
    [groupedGroupsByEntity],
  );

  const [groupedDefinitions, setGroupedDefinitions] = useState(
    groupedGroupsByEntityAndPartner,
  );

  const formMethods = useForm<
    Record<string, Record<number, Partial<CustomAttributeGroup>[] | undefined>>
  >({
    shouldFocusError: true,
    mode: 'all',
    defaultValues: groupedGroupsByEntityAndPartner ?? null,
  });

  useEffect(() => {
    formMethods.reset(groupedGroupsByEntityAndPartner);
    setGroupedDefinitions(groupedGroupsByEntityAndPartner);
    // only re-sync when the underlying GraphQL data changes, depending on
    // the memoized `groupedGroupsByEntityAndPartner` would loop because each
    // upstream render produces a new object reference
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customAttributeDefinitions]);

  const { showSuccessAlert, showErrorAlert } = useNotifications();

  const onSubmit = useCallback(
    async (
      data: Record<string, Record<number, Partial<CustomAttributeGroup>[] | undefined>>,
    ) => {
      if (isNullish(selectedPartnerId)) {
        return;
      }
      const errors: string[] = [];

      const promises = Object.entries(data).map(([entity, partners]) => {
        const groups = partners[selectedPartnerId];

        const errs = checkGroupsErrors(groups);

        if (errs) {
          errors.push(errs);
          return;
        }

        const currentPartnerGroups = groups?.filter(
          (group) => group.partnerId === selectedPartnerId,
        );

        const customAttributeDefinition = customAttributeDefinitions.find(
          (attributeDefinition) =>
            attributeDefinition.entity === entity &&
            attributeDefinition.partnerId === selectedPartnerId,
        );

        let definition = {
          attributes: {},
        } as AttributeDefinition;

        if (customAttributeDefinition?.definition) {
          definition =
            parseJsonSafely<AttributeDefinition>(customAttributeDefinition.definition) ||
            definition;
        }

        definition.groups =
          currentPartnerGroups?.map((group) => ({
            displayName: group.displayName?.trim() ?? '',
            key: slugify(group.displayName?.trim()),
          })) ?? [];

        return saveCustomAttributeDefinition({
          customAttributeDefinition,
          definition: JSON.stringify(definition),
          entity,
          partnerId: selectedPartnerId,
        });
      });

      if (errors.length) {
        showErrorAlert(errors[0]);
        return;
      }

      closeModal();

      const responses = await Promise.all(promises);

      if (responses.some((response) => !response)) {
        return;
      }

      showSuccessAlert('Groups updated successfully');
    },
    [
      selectedPartnerId,
      closeModal,
      showSuccessAlert,
      customAttributeDefinitions,
      saveCustomAttributeDefinition,
      showErrorAlert,
    ],
  );

  useEffect(() => {
    const { unsubscribe } = formMethods.watch((value) => {
      setGroupedDefinitions(
        value as Record<string, Record<number, CustomAttributeGroup[] | undefined>>,
      );
    });

    return () => unsubscribe();
  }, [formMethods]);

  return (
    <ModalContainer
      open={isOpened}
      size="large"
      title="Manage custom property groups"
      actions={[
        { label: 'Cancel', kind: 'secondary', testId: 'manage-custom-attribute-group-cancel' },
        {
          label: 'Save groups',
          kind: 'primary',
          onClick: formMethods.handleSubmit(onSubmit),
          disabled: !formMethods.formState.isValid,
          testId: 'manage-custom-attribute-group-submit',
        },
      ]}
      onClose={closeModal}
      data-testid="manage-custom-attribute-group-modal"
    >
      <GenericForm formMethods={formMethods}>
        {platformEntities.map((entity) => (
          <EntityContainer
            key={entity.id}
            entity={entity}
            partners={groupedDefinitions[entity.id]}
          />
        ))}
      </GenericForm>
    </ModalContainer>
  );
};

function checkGroupsErrors(groups?: Partial<CustomAttributeGroup>[]) {
  const hasInvalidValues = groups?.some((item) => !item.displayName) ?? false;

  if (hasInvalidValues) {
    return 'There are some empty values';
  }

  const hasDuplicatedValues =
    groups?.some((item, idx, currentArray) => {
      return (
        currentArray.findIndex(
          (currentItem) => currentItem.displayName === item.displayName,
        ) !== idx
      );
    }) ?? false;

  if (hasDuplicatedValues) {
    return 'There are some duplicated elements';
  }

  return '';
}
